'use client';

/**
 * @fileoverview `CountUpNumber` — a self-contained client atom that animates a
 * single integer from 0 → `value` the moment it scrolls into view, mirroring
 * the DS v1.5.0 city template `[data-count]` behaviour
 * (`docs/design-system/1.5.0/templates/city/index.html`). Wraps the
 * SSR-safe, `prefers-reduced-motion`-aware {@link useCountUp} hook so any
 * server-rendered surface can drop a live-counting stat in without owning the
 * `IntersectionObserver` / `requestAnimationFrame` plumbing.
 *
 * **SSR-safe, no hydration flash (no React #418).** `useCountUp` seeds its
 * state to the final `value`, so the server render, the first client
 * (hydration) render, crawlers, and no-JS users all paint the real number —
 * the markup matches across the hydration boundary. Only after mount, and only
 * when JS + `IntersectionObserver` are present *and* motion is not reduced,
 * does the element reset to 0 and ramp up on scroll-in. The reset-to-0 is a
 * post-commit client state update, never a hydration-time mismatch.
 *
 * **Reduced motion.** When `prefers-reduced-motion: reduce` is set the hook
 * never resets to 0 — the final `value` stays painted from first byte. No
 * essential information is conveyed only by the animation; the number is always
 * present.
 *
 * The element is rendered as a `<span>` (an `<output>`-style live stat) with the
 * canonical `data-count` attribute so it matches the DS template markup and is
 * greppable. Visible digits format through {@link formatCount} every frame
 * (WEB-942). `role="img"` + `aria-label` announce the **final** grouped number
 * so AT never hears an intermediate ramp (`1,113`…`1,391`).
 *
 * @since FRE-919 (Discover live motion · count-up stats — DS 1.5.0, scope A)
 */

import * as React from 'react';

import { useCountUp } from '@/hooks/useCountUp';
import { formatCount } from '@/lib/discover/formatCount';

/** Props for {@link CountUpNumber}. */
export interface CountUpNumberProps {
  /** Final integer value to count up to (and the SSR / first-paint value). */
  readonly value: number;
  /** Total animation duration in milliseconds. Forwarded to `useCountUp`. */
  readonly durationMs?: number;
  /** `IntersectionObserver` visibility threshold. Forwarded to `useCountUp`. */
  readonly threshold?: number;
  /** Additional CSS classes merged onto the `<span>`. */
  readonly className?: string;
  /** Optional test id. Defaults to `count-up-number`. */
  readonly 'data-testid'?: string;
}

/**
 * Animate an integer from 0 → `value` on scroll-in. SSR-safe and
 * `prefers-reduced-motion` aware (delegated to {@link useCountUp}).
 *
 * @example
 * ```tsx
 * <CountUpNumber value={42} className="font-semibold" />
 * ```
 */
export function CountUpNumber({
  value,
  durationMs,
  threshold,
  className,
  'data-testid': dataTestId = 'count-up-number',
}: CountUpNumberProps): React.ReactElement {
  const { ref, value: current } = useCountUp<HTMLSpanElement>({
    target: value,
    durationMs,
    threshold,
  });
  const announced = formatCount(value);

  return (
    <span
      ref={ref}
      data-count={value}
      className={className}
      data-testid={dataTestId}
      role="img"
      aria-label={announced}
    >
      {formatCount(current)}
    </span>
  );
}
